import React, { useEffect, useRef, useState } from "react";
import EditIcon from "../../../ui/EditIcon";
import { Spin, Button, message, theme, Space, Tag } from "antd";
import {
  getServiceAPI,
  updatePhysioCareAPI,
  updatePhysioInspectionAPI,
  updatePhysioReviewAPI,
} from "../../../../services/physio.services";
import CardioSummary from "../Forms/CardiopulmonaryForm/CardioSummary";
import NeuroSummary from "../Forms/NeuroForm/NeuroSummary";
import OrthoSummary from "../Forms/Orthoform/OrthoSummary";
import SportsSummary from "../Forms/SportsForm/SportsSummary";
import CardioForm from "../Forms/CardiopulmonaryForm/CardiopulmonaryForm";
import NeuroForm from "../Forms/NeuroForm/NeuroForm";
import OrthoForm from "../Forms/Orthoform/OrthoForm";
import SportsForm from "../Forms/SportsForm/SportsForm";

import btnStyles from "../../../../styles/Custom/Button.module.css";
import "../../../PatientsOP/patientHeader.css";

const { useToken } = theme;
const serviceUpdateMap = {
  physio_care: updatePhysioCareAPI,
  cardiopulmonary: updatePhysioInspectionAPI,
  cardio: updatePhysioInspectionAPI,
  neuro: updatePhysioReviewAPI,
  ortho: updatePhysioReviewAPI,
  sports: updatePhysioReviewAPI,
  ia: updatePhysioCareAPI,
};
const serviceLabelMap = {
  cardiopulmonary: "History",
  cardio: "History",
  neuro: "Functional Status Assessment",
  ortho: "Orthopedic Assessment",
  sports: "Sports Assessment",
  physio_care: "Physio Care",
  ia: "Initial Assessment",
};
const DisplaySummary = ({
  currentEmr,
  refreshPage,
  handleBack,
  hideEdit,
  patientData,
}) => {
  if (!currentEmr || currentEmr.length === 0) return null;

  return (
    <div>
      {currentEmr.map((emrItem) => (
        <EMRItem
          key={emrItem.id}
          emrItem={emrItem}
          refreshPage={refreshPage}
          handleBack={handleBack}
          hideEdit={hideEdit}
          patientData={patientData}
        />
      ))}
    </div>
  );
};

const EMRItem = ({
  emrItem,
  refreshPage,
  handleBack,
  hideEdit,
  patientData,
}) => {
  const { token } = useToken();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const service =
    emrItem?.service_type === "cardio"
      ? "cardiopulmonary"
      : emrItem?.service_type;

  useEffect(() => {
    setLoading(true);
    getServiceAPI(service, emrItem.id)
      .then((res) => setFormData(res?.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [service, emrItem.id]);

  const handleEditClick = () => setEditMode(true);

  const handleCancel = async () => {
    if (service === "ia" && handleBack) {
      handleBack();
    } else {
      setLoading(true);
      try {
        const res = await getServiceAPI(service, emrItem.id);
        setFormData(res?.data);
      } catch (error) {
        console.error("Failed to refresh data after cancel:", error);
      } finally {
        setLoading(false);
        setEditMode(false);
      }
    }
  };

  const handleCancelRef = useRef(handleCancel);
  handleCancelRef.current = handleCancel;

  useEffect(() => {
    const onOpHeaderBack = (e) => {
      if (!editMode) return;
      e.preventDefault();
      void handleCancelRef.current();
    };
    window.addEventListener("opPatientHeaderBack", onOpHeaderBack);
    return () =>
      window.removeEventListener("opPatientHeaderBack", onOpHeaderBack);
  }, [editMode]);

  const handleSave = (updatedData) => {
    setFormData(updatedData);
    setEditMode(false);
    // const sectionName = serviceLabelMap[service] || "Record";
    // message.success(`${sectionName} submitted successfully`);
    if (refreshPage) refreshPage();
  };

  const handleFormSubmit = async (values) => {
    try {
      const updateFn = serviceUpdateMap[service];
      if (!updateFn) throw new Error(`No update API defined for ${service}`);
      const updatedData = await updateFn(emrItem.id, values);
      handleSave(updatedData);
    } catch (error) {
      console.error(error);
      message.error("Something went wrong!");
    }
  };

  const renderForm = () => {
    if (!formData) return null;

    const formProps = {
      formData,
      handleClose: handleCancel,
      onSave: handleSave,
      onSubmit: handleFormSubmit,
      admissionId: emrItem?.admission_id || null,
      formId: emrItem?.id || null,
      patientData,
    };

    switch (service) {
      case "cardiopulmonary":
        return <CardioForm {...formProps} />;
      case "neuro":
        return <NeuroForm {...formProps} />;
      case "ortho":
        return <OrthoForm {...formProps} />;
      case "sports":
        return <SportsForm {...formProps} />;
      case "physio_care":
      case "ia":
        return <CardioForm {...formProps} />;
      default:
        return <div>No form available for this service.</div>;
    }
  };

  const renderSummary = () => {
    switch (service) {
      case "cardiopulmonary":
        return <CardioSummary formData={formData} />;
      case "neuro":
        return <NeuroSummary formData={formData} />;
      case "ortho":
        return <OrthoSummary formData={formData} />;
      case "sports":
        return <SportsSummary formData={formData} />;
      case "physio_care":
      case "ia":
        return <CardioSummary formData={formData} />;
      default:
        return <div>No summary available.</div>;
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {!editMode && !hideEdit && (
        <div
          className="emr-edit-wrapper"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div
            className="emr-submitted-tags"
            style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
          >
            <Space size={8} wrap>
              <Tag
                style={{
                  padding: "6px 12px",
                  fontSize: 13,
                  borderRadius: 6,
                }}
              >
                Submitted by: {emrItem?.doctor_name || "N/A"}
              </Tag>

              <Tag
                style={{
                  padding: "6px 12px",
                  fontSize: 13,
                  borderRadius: 6,
                }}
              >
                Updated at:{" "}
                {emrItem?.created_at
                  ? new Date(emrItem.created_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : "N/A"}
              </Tag>
            </Space>
          </div>

          <Button
            type="primary"
            onClick={handleEditClick}
            className={`${btnStyles.lgBtn} inv-btn`}
            style={{ fontWeight: 500 }}
          >
            <EditIcon />
            Edit
          </Button>
        </div>
      )}

      {loading ? <Spin /> : editMode ? renderForm() : renderSummary()}
    </div>
  );
};

export default DisplaySummary;
